import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { fireToast } from '../../../../../helpers/Toasts';
import { openOrNavigate } from '../../../../../helpers/openOrNavigate';
import { accountsApi } from '../../../../../api/accountsApi';
import {
  SELLER_ANALYTICS_CATEGORIES,
  SELLER_ANALYTICS_TIME_RANGES,
} from '../../../../../types';
import { formatAuthorizationHeader } from '../../../../../helpers/auth';
import { pushToDataLayer } from '../../../../../services/gtmService';
import { useAdDetailsContext } from '../../../../../contexts/AdDetailsContext';
import { useUserContext } from '../../../../../contexts/UserContext';
import { useSellerAnalyticsModal } from '../../../../../contexts/SellerAnalyticsModalContext';
import type { useAdTitleBoxProps } from '../../../../../components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox.typed';
import { logger } from '@dist-marketplace-frontend/services';
import { PAGE } from '../../../../../helpers/pages';
import { getPriceDisplay } from '../../../../../helpers/getPriceDisplay';
import {
  generateFinanceLink,
  getFinanceCtaUrl,
} from '../../../../../components/PageWrappers/AdDetails/helpers';

const useAdTitleBox = (props: useAdTitleBoxProps) => {
  const {
    adId,
    carFinance,
    currency,
    financeInfo,
    financeLink,
    price,
    priceOnRequest,
    title,
  } = props;

  const { accessToken } = useUserContext();
  const { setIsPricePerMonthModalOpen } = useAdDetailsContext();
  const { setIsSellerAnalyticsModalOpen, setSellerAnalyticsModalDisplay } =
    useSellerAnalyticsModal();
  const router = useRouter();

  const {
    publicRuntimeConfig: { FEATURE_CAR_FINANCE_UPDATES },
  } = getConfig();

  async function handleAnalyticsView() {
    try {
      const { data } = await accountsApi.getSellerAnalyticsData(
        adId,
        SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS,
        formatAuthorizationHeader(accessToken),
      );
      setIsSellerAnalyticsModalOpen(true);
      setSellerAnalyticsModalDisplay({
        title,
        selectedTab: SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
        data: data.plotValues,
      });
      pushToDataLayer({
        event: 'seller_analytics',
        event_category: 'ad_management',
        event_action: 'view_ad_analytics',
        event_label: 'ad details page',
      });
    } catch (error) {
      logger.error({
        message: 'Error getting seller analytics data',
        error,
        tags: [PAGE.AD_DETAILS],
        customData: { functionName: 'handleAnalyticsView' },
      });
      fireToast({
        type: 'ERROR',
        text: 'Oops, something went wrong! Please try again later',
      });
    }
  }

  const hasFinanceLink = Boolean(financeLink || financeInfo);
  const ctaUrl = financeInfo
    ? getFinanceCtaUrl(financeInfo, FEATURE_CAR_FINANCE_UPDATES)
    : '';
  const isNewFinanceFlow = Boolean(
    FEATURE_CAR_FINANCE_UPDATES && financeInfo?.resolvedRedirectURL,
  );
  // For Toyota the BE also returns a top-level `financeLink` that points
  // straight at the partner. When FF is on and a resolvedRedirectURL exists,
  // prefer the new flow so the user goes through the DD short form first.
  const finance = {
    text: financeLink?.title || 'Get Finance Approval',
    url: (isNewFinanceFlow ? ctaUrl : '') || financeLink?.url || ctaUrl || '',
  };

  function handleFinanceLinkClick() {
    pushToDataLayer({
      event: 'partner_clicks',
      servicePartnerType: carFinance?.banner?.partnerType,
      servicePartnerName: carFinance?.banner?.partnerName,
    });
  }

  const financeClassImp = `${carFinance?.banner?.partnerName}-${carFinance?.banner?.partnerType}-imp`;

  const { priceText, isValidPrice } = getPriceDisplay({
    price,
    priceOnRequest: priceOnRequest ?? false,
    currency,
  });

  const isPriceOnRequest = !isValidPrice;

  const isDirectLinkPartner =
    financeInfo?.partnerName === 'bankofireland' ||
    financeInfo?.partnerName === 'closebrothers';

  const trackPPMPartnerClick = () => {
    if (!financeInfo) return;
    pushToDataLayer({
      event: 'partner_clicks',
      servicePartnerType: financeInfo.partnerType,
      servicePartnerName: financeInfo.partnerName,
    });
  };

  const handlePPMClick = () => {
    if (isNewFinanceFlow) {
      trackPPMPartnerClick();
      openOrNavigate(ctaUrl, router.query.platform);
      return;
    }
    if (financeInfo && isDirectLinkPartner) {
      trackPPMPartnerClick();
      const redirectUrl = generateFinanceLink(financeInfo);
      if (financeInfo.external) {
        openOrNavigate(redirectUrl, router.query.platform);
      } else {
        window.open(redirectUrl);
      }
      return;
    }
    setIsPricePerMonthModalOpen(true);
  };

  return {
    handleAnalyticsView,
    hasFinanceLink,
    finance,
    handleFinanceLinkClick,
    financeClassImp,
    priceText,
    isPriceOnRequest,
    handlePPMClick,
  };
};

export { useAdTitleBox };
